import { useState, useEffect } from 'react';
import axios from 'axios';
import { CONFIG } from 'variables/config';

interface SubIndustry {
    name: string;
    sub?: string[];
}

export interface Industry {
    _id: string;
    name: string;
    description?: string;
    forWho?: string;
    pricing: string[];
    fields: string[];
    mandatoryFields: string[];
    steps?: number;
    subIndustries: SubIndustry[];
    createdAt: string;
    updatedAt: string;
}

// Cache for industries
let industriesCache: Industry[] | null = null;
let cacheTimestamp: number | null = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch all industries from API
 * Uses caching to reduce API calls
 */
export async function fetchIndustries(forceRefresh = false): Promise<Industry[]> {
    const now = Date.now();
    
    // Return cached data if available and not expired
    if (!forceRefresh && industriesCache && cacheTimestamp && (now - cacheTimestamp) < CACHE_DURATION) {
        return industriesCache;
    }

    try {
        const response = await axios.get(`${CONFIG.apiUrl}/industry`);
        industriesCache = response.data;
        cacheTimestamp = now;
        return response.data;
    } catch (error) {
        console.error('Error fetching industries:', error);
        // Return cached data if available, even if expired
        if (industriesCache) {
            return industriesCache;
        }
        throw error;
    }
}

/**
 * Fetch a single industry by ID
 */
export async function fetchIndustryById(id: string): Promise<Industry> {
    const response = await axios.get(`${CONFIG.apiUrl}/industry/${id}`);
    return response.data;
}

/**
 * Fetch industry by name
 */
export async function fetchIndustryByName(name: string): Promise<Industry> {
    const response = await axios.get(`${CONFIG.apiUrl}/industry/name/${encodeURIComponent(name)}`);
    return response.data;
}

/**
 * Clear the industries cache (useful after creating/updating/deleting)
 */
export function clearIndustriesCache() {
    industriesCache = null;
    cacheTimestamp = null;
}

/**
 * React hook to use industries in components
 */
export function useIndustries() {
    const [industries, setIndustries] = useState<Industry[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const loadIndustries = async (forceRefresh = true) => {
        try {
            setLoading(true);
            const data = await fetchIndustries(forceRefresh);
            setIndustries(data);
            setError(null);
        } catch (err: any) {
            setError(err.message || 'Failed to load industries');
            console.error('Error loading industries:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadIndustries();
    }, []);

    return {
        industries,
        loading,
        error,
        refresh: () => loadIndustries(true),
    };
}

/**
 * Helper to convert legacy format to new format
 * For backward compatibility during migration
 */
export function convertIndustriesToLegacyFormat(industries: Industry[]): any {
    const industriesPricing: any = {};
    const industriesMap: any = {};

    industries.forEach((industry) => {
        // Build pricing config
        industriesPricing[industry.name] = {
            description: industry.description || '',
            forWho: industry.forWho || '',
            pricing: industry.pricing,
            fields: industry.fields,
            mandatoryFields: industry.mandatoryFields,
            steps: industry.steps,
        };

        // Build industries map
        industriesMap[industry.name] = industry.subIndustries;
    });

    return { industriesPricing, industries: industriesMap };
}
